import { useTranslation } from 'react-i18next';
import { S, Icon, TH, TD, EmptyState, formatDate } from '../regureStyles';
import { docTypeLabel } from '../../utils/docTypeLabels';

const CONFIDENCE_COLOR = c => (c >= 0.9 ? '#10b981' : c >= 0.7 ? '#f59e0b' : '#ef4444');

// Row actions adapt to the tenant's enabled features. Both "Link to Claim"
// and "Attach to Client" are optional; a tenant with neither enabled sees
// no action button (data-only view).
const FilesTable = ({
  loading,
  filtered,
  setSelected,
  setLinkClaimId,
  canLinkToClaim,
  canLinkToClient,
  setClientPickerFor,
  setLinkClientId,
  loadClientsForPicker,
}) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language || 'en').split('-')[0];
  return (
    <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, overflow: 'hidden' }}>
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: S.textMuted }}>{t('screens.unmatchedDocs.loading')}</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon='file-text' title={t('screens.unmatchedDocs.emptyTitle')} description={t('screens.unmatchedDocs.emptyDescription')} />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: S.bgCard }}>
              <TH>{t('screens.unmatchedDocs.col.filename')}</TH>
              <TH>{t('screens.unmatchedDocs.col.documentType')}</TH>
              <TH>{t('screens.unmatchedDocs.col.aiConfidence')}</TH>
              <TH>{t('screens.unmatchedDocs.col.extractedFields')}</TH>
              <TH>{t('screens.unmatchedDocs.col.pii')}</TH>
              <TH>{t('screens.unmatchedDocs.col.uploaded')}</TH>
              <TH>{t('screens.unmatchedDocs.col.action')}</TH>
            </tr>
          </thead>
          <tbody>
            {filtered.map((doc, i) => {
              const rawType = doc.documentType || doc.classification?.documentType || null;
              // Backend stores canonical snake_case keys. If it matches a
              // known key, translate; otherwise fall back to the localised
              // "Unknown" so French/German users don't see the English word.
              const type = rawType ? docTypeLabel(rawType, lang) : t('screens.unmatchedDocs.unknownType');
              const conf = doc.confidence || doc.classification?.confidence || 0;
              const fields = doc.extractedFields || doc.classification?.extractedFields || {};
              const filename = doc.filename || doc.originalName || t('screens.unmatchedDocs.unknownFile');
              return (
                <tr
                  key={doc._id}
                  style={{ borderTop: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard }}
                  onMouseEnter={e => (e.currentTarget.style.background = S.bgHover)}
                  onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard)}
                >
                  <TD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Icon name='file-text' size={15} color={S.textMuted} />
                      <span
                        style={{ fontWeight: 500, color: S.textPrimary, fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {filename}
                      </span>
                    </div>
                  </TD>
                  <TD>
                    <span style={{ padding: '3px 8px', background: `${S.burgundy}10`, color: S.burgundy, borderRadius: 4, fontSize: 12, fontWeight: 500 }}>{type}</span>
                  </TD>
                  <TD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 60, height: 6, background: S.bgCard, borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${conf * 100}%`, background: CONFIDENCE_COLOR(conf), borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: CONFIDENCE_COLOR(conf) }}>{Math.round(conf * 100)}%</span>
                    </div>
                  </TD>
                  <TD>
                    <div style={{ fontSize: 11, color: S.textMuted, fontFamily: 'monospace' }}>
                      {Object.keys(fields).length === 0
                        ? '—'
                        : Object.entries(fields)
                            .slice(0, 2)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(' · ')}
                    </div>
                  </TD>
                  <TD>
                    {doc.piiRedacted ? (
                      <span style={{ padding: '2px 7px', background: '#ecfdf5', color: '#10b981', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{t('screens.unmatchedDocs.pii.redacted')}</span>
                    ) : (
                      <span style={{ padding: '2px 7px', background: '#fef2f2', color: '#ef4444', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{t('screens.unmatchedDocs.pii.raw')}</span>
                    )}
                  </TD>
                  <TD>
                    <span style={{ fontSize: 12, color: S.textMuted }}>{formatDate(doc.uploadedAt || doc.createdAt)}</span>
                  </TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {/* Open — always present when doc._id exists. Same */}
                      {/* endpoint the ClaimDetail DocumentsTab uses; it */}
                      {/* resolves the S3 key server-side (s3Key / */}
                      {/* contentKey / fileUrl) so we don't gate on those. */}
                      {doc._id && (
                        <button
                          onClick={() => window.open(`/api/v1/documents/${doc._id}/view`, '_blank', 'noopener,noreferrer')}
                          style={{
                            padding: '6px 12px',
                            border: `1px solid ${S.textMuted}40`,
                            borderRadius: 6,
                            background: '#fff',
                            color: S.textPrimary,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {t('screens.unmatchedDocs.action.openPdf', { defaultValue: 'Open PDF' })}
                        </button>
                      )}
                      {canLinkToClaim && (
                        <button
                          onClick={() => {
                            setSelected(doc);
                            setLinkClaimId(doc.extractedFields?.claimNumber || '');
                          }}
                          style={{
                            padding: '6px 12px',
                            border: `1px solid ${S.burgundy}40`,
                            borderRadius: 6,
                            background: `${S.burgundy}08`,
                            color: S.burgundy,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {t('screens.unmatchedDocs.action.linkToClaim')}
                        </button>
                      )}
                      {canLinkToClient && (
                        <button
                          onClick={() => {
                            if (loadClientsForPicker) loadClientsForPicker();
                            if (setLinkClientId) setLinkClientId('');
                            if (setClientPickerFor) setClientPickerFor(doc);
                          }}
                          style={{
                            padding: '6px 12px',
                            border: `1px solid ${S.info}40`,
                            borderRadius: 6,
                            background: `${S.info}08`,
                            color: S.info,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {t('screens.unmatchedDocs.action.attachToClient')}
                        </button>
                      )}
                      {!doc._id && !canLinkToClaim && !canLinkToClient && (
                        <span style={{ fontSize: 11, color: S.textMuted, fontStyle: 'italic' }}>—</span>
                      )}
                    </div>
                  </TD>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default FilesTable;
